import { useEffect, useState } from "react";
import { Frame, FrameHeader, FramePanel, FrameTitle, FrameAction } from "@/components/ui/frame";
import { ArrowRightIcon, CircleCheckBigIcon, MailIcon, TriangleAlertIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { DubDomain, PartnershipTier, StatusValue } from "@/types/types";
import { useAuth } from "@clerk/react";
import { useFundingStore } from "@/stores/eve/fundingStore";
import {
  useCreatorStatusStore,
  useCreatorsStore,
  useDashboardStore,
  useEveActionsStore,
} from "@/stores";
import { isEveCampaign } from "@/lib/eve";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import posthog from "posthog-js";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreatorsTableContext, useCreatorsTableState } from "../CreatorsTableContext";
import { summarizeTier } from "../columns/TierColumnCell";
import { extractCustomActivationVariables } from "@/components/dashboard_campaign/brief/MessageVariables";
import { formatCurrency } from "@/lib/format";

/** amy's GRADE_FEE_RATE default — used until the funding snapshot says otherwise. */
const DEFAULT_PROCESSING_FEE_RATE = 0.05;

function renderActivationPreview(
  text: string,
  previews: Record<string, string>,
): React.ReactNode[] {
  const regex = /\{([a-z][a-z0-9_]*)\}/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const varName = match[1];
    if (varName in previews) {
      const value = previews[varName];
      parts.push(
        value ? (
          <span
            key={`v-${key++}`}
            className="underline underline-offset-2 decoration-dashed font-medium text-foreground"
          >
            {value}
          </span>
        ) : (
          <span
            key={`v-${key++}`}
            className="underline underline-offset-2 decoration-dashed text-muted-foreground italic"
          >
            {`{${varName}}`}
          </span>
        ),
      );
    } else {
      parts.push(match[0]);
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
}

/**
 * Presentational, fully prop-driven activation dialog. Shared by the campaign
 * creators table (via the `ActivateCreatorDialog` adapter below) and the Tasks
 * dashboard's waiting-approval action. Owns only the local input state; all data
 * and the confirm side effect are supplied by the caller.
 */
export interface ActivateCreatorDialogViewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  username: string;
  useDub: boolean;
  ccEmails: string[] | string;
  currentTierOrder: number;
  tiers: PartnershipTier[];
  negotiatedPrice?: number | null;
  currency?: string;
  dubDomains: DubDomain[];
  campaignId: string;
  /** Eve campaigns only: surface the client-funding headroom warning (non-blocking). */
  checkFunding?: boolean;
  onConfirm: (args: {
    promoCode: string;
    manualUniqueLink: string;
    selectedDomain: string;
    extraVariables: Record<string, string>;
  }) => void | Promise<void>;
}

export function ActivateCreatorDialogView({
  open,
  onOpenChange,
  username,
  useDub,
  ccEmails,
  currentTierOrder,
  tiers,
  negotiatedPrice,
  currency,
  dubDomains,
  campaignId,
  checkFunding,
  onConfirm,
}: ActivateCreatorDialogViewProps) {
  const { getToken } = useAuth();
  const fetchFunding = useFundingStore((s) => s.fetchFunding);
  const fundingMode = useFundingStore((s) => s.mode);
  const fundingSnapshot = useFundingStore((s) => s.funding);

  // Platform-wallet orgs: refresh the pot when the dialog opens so the
  // headroom warning below reflects reality. Approval is never blocked —
  // the hard gate lives at payout confirm.
  useEffect(() => {
    if (!open || !checkFunding) return;
    void (async () => {
      const token = await getToken();
      if (token) await fetchFunding(token);
    })();
  }, [open, checkFunding, getToken, fetchFunding]);

  const [manualUniqueLink, setManualUniqueLink] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [selectedDomain, setSelectedDomain] = useState("");
  const [extraValues, setExtraValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const campaignCcEmails: string[] = Array.isArray(ccEmails)
    ? ccEmails
    : typeof ccEmails === "string"
      ? (() => {
          try {
            const parsed = JSON.parse(ccEmails);
            return Array.isArray(parsed) ? parsed : [];
          } catch {
            return [];
          }
        })()
      : [];

  const currentTier = tiers.find((t) => t.order === currentTierOrder);
  const tierActivationMessage =
    currentTier?.activation_message ||
    tiers.find((t) => t.order === 1)?.activation_message ||
    "";
  const negotiatedPriceStr =
    negotiatedPrice != null ? String(negotiatedPrice) : undefined;
  const tierSummary = currentTier
    ? summarizeTier(currentTier, negotiatedPriceStr, currency)
    : "";

  const dubSlug = selectedDomain || dubDomains[0]?.slug || "";
  const uniqueLinkPreview = useDub
    ? username && dubSlug
      ? `https://${dubSlug}/${username}_****`
      : ""
    : manualUniqueLink;

  // Custom {tokens} in the activation message become extra inputs
  // (not supported on Dub campaigns — the promo/link inputs are hidden there too)
  const customVariables = useDub
    ? []
    : extractCustomActivationVariables(tierActivationMessage);

  const variablePreviews: Record<string, string> = {
    ...Object.fromEntries(customVariables.map((name) => [name, ""])),
    ...extraValues,
    current_tier_order: tierSummary,
    creator_username: username || "",
    promo_code: promoCode,
    unique_link: uniqueLinkPreview,
  };

  const resetInputs = () => {
    setManualUniqueLink("");
    setPromoCode("");
    setSelectedDomain("");
    setExtraValues({});
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) resetInputs();
    onOpenChange(next);
  };

  const handleActivate = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onConfirm({
        promoCode,
        manualUniqueLink,
        selectedDomain,
        extraVariables: extraValues,
      });
    } finally {
      setSubmitting(false);
      resetInputs();
      onOpenChange(false);
    }
  };

  const price = negotiatedPrice ?? 0;
  // Payouts debit amount + Grade's fee, so headroom compares gross figures:
  // the server's committed_gross plus this deal at the same estimated rate.
  const feeRate = fundingSnapshot?.fee_rate ?? 0;
  const committedGross = fundingSnapshot?.committed_gross ?? fundingSnapshot?.committed ?? 0;
  const projectedGross = Math.round((committedGross + price * (1 + feeRate)) * 100) / 100;
  const fundingWarning =
    checkFunding && fundingMode === "platform" && fundingSnapshot && price > 0
      ? fundingSnapshot.available <= 0
        ? {
            tone: "destructive" as const,
            text: `No client funding available — approving is fine, but the $${price.toLocaleString()} payout will hold until a deposit is recorded.`,
          }
        : projectedGross > fundingSnapshot.available
          ? {
              tone: "warning" as const,
              text: `This approval takes committed deals to $${projectedGross.toLocaleString()} incl. est. fees against $${fundingSnapshot.available.toLocaleString()} of client funding — the last payouts may hold until more lands.`,
            }
          : null
      : null;

  // What approving costs the client: the agreed creator fee plus the payment
  // processing fee on payouts Uplodio runs (Grade). Legacy campaigns and orgs
  // without Grade pay creators themselves, so they get no processing line.
  const processingRate =
    checkFunding && fundingMode !== "none"
      ? (fundingSnapshot?.fee_rate ?? DEFAULT_PROCESSING_FEE_RATE)
      : 0;
  const processingFee = Math.round(price * processingRate * 100) / 100;
  const totalPrice = Math.round((price + processingFee) * 100) / 100;
  const processingPct = `${(processingRate * 100).toFixed(1).replace(/\.0$/, "")}%`;
  const money = (n: number) =>
    formatCurrency(
      Number.isInteger(n)
        ? n
        : n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      currency,
    );

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="max-w-3xl overflow-auto">
        <AlertDialogHeader>
          <AlertDialogTitle>Activate creator with this info:</AlertDialogTitle>
          <AlertDialogDescription>
            {useDub
              ? "Amy will send the creator their unique Dub link along with the activation message."
              : "Optionally add a unique link and/or promo code for this creator. They will be sent along the activation message."}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {fundingWarning && (
          <Alert variant={fundingWarning.tone}>
            <AlertTitle>
              <TriangleAlertIcon /> Client funding
            </AlertTitle>
            <AlertDescription>{fundingWarning.text}</AlertDescription>
          </Alert>
        )}

        <div className="flex w-full flex-col gap-5">
          {price > 0 && (
            <Frame>
              <FrameHeader>
                <FrameTitle>Final price</FrameTitle>
              </FrameHeader>
              <FramePanel className="flex flex-col divide-y p-0 overflow-hidden">
                <PriceRow label="Creator fee" value={money(price)} />
                {processingRate > 0 && (
                  <>
                    <PriceRow
                      label={`Payment processing (${processingPct})`}
                      value={money(processingFee)}
                    />
                    <PriceRow label="Total" value={money(totalPrice)} strong />
                  </>
                )}
              </FramePanel>
            </Frame>
          )}

          <div className="flex md:flex-row flex-col gap-5 w-full">
            <div className="flex flex-col gap-2 w-full">
              <div className="flex items-center justify-between w-full">
                <Label>Unique Link for this creator</Label>
                {!useDub && (
                  <Label className="text-muted-foreground text-xs font-normal">
                    Optional
                  </Label>
                )}
              </div>
              {useDub ? (
                <div className="flex w-full h-9">
                  <Select
                    defaultValue={dubDomains[0]?.slug}
                    onValueChange={(value) => setSelectedDomain(value)}
                  >
                    <SelectTrigger className="w-auto rounded-r-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {dubDomains.map((domain) => (
                        <SelectItem key={domain.id} value={domain.slug}>
                          https://{domain.slug}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    value={`/${username}_****`}
                    readOnly
                    className="rounded-l-none flex-1 border-l-0"
                  />
                </div>
              ) : (
                <Input
                  value={manualUniqueLink}
                  onChange={(e) => setManualUniqueLink(e.target.value)}
                  placeholder="https://dub.sh/tIZmwhS"
                />
              )}
            </div>

            {!useDub && (
              <div className="flex flex-col gap-2 w-full">
                <div className="flex items-center justify-between w-full">
                  <Label>Promo code for this creator</Label>
                  <Label className="text-muted-foreground text-xs font-normal">
                    Optional
                  </Label>
                </div>
                <Input
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  placeholder="Enter promo code"
                />
              </div>
            )}
          </div>

          {customVariables.length > 0 && (
            <div className="flex md:flex-row flex-col gap-5 w-full">
              {customVariables.map((name) => (
                <div key={name} className="flex flex-col gap-2 w-full">
                  <div className="flex items-center justify-between w-full">
                    <Label className="capitalize">
                      {name.replace(/_/g, " ")} for this creator
                    </Label>
                    <Label className="text-muted-foreground text-xs font-normal font-mono">
                      {`{${name}}`}
                    </Label>
                  </div>
                  <Input
                    value={extraValues[name] ?? ""}
                    onChange={(e) =>
                      setExtraValues((prev) => ({
                        ...prev,
                        [name]: e.target.value,
                      }))
                    }
                    placeholder={`Enter ${name.replace(/_/g, " ")}`}
                  />
                </div>
              ))}
            </div>
          )}

          <Frame>
            <FrameHeader>
              <FrameTitle>CC emails</FrameTitle>
              <FrameAction>
                <EditLink to={`/dashboard/campaigns/${campaignId}/settings/messaging`}>
                  Edit CC emails
                </EditLink>
              </FrameAction>
            </FrameHeader>
            <FramePanel className="flex flex-wrap gap-2 p-3">
              {campaignCcEmails.length > 0 ? (
                campaignCcEmails.map((email) => (
                  <Badge key={email} variant="ghost">
                    <MailIcon />
                    {email}
                  </Badge>
                ))
              ) : (
                <span className="text-xs italic text-muted-foreground">
                  No default CC emails set for this campaign.
                </span>
              )}
            </FramePanel>
          </Frame>

          <Frame>
            <FrameHeader>
              <FrameTitle>Tier {currentTierOrder} activation message</FrameTitle>
              <FrameAction>
                <EditLink
                  to={`/dashboard/campaigns/${campaignId}/brief/activation_message_tier_${currentTierOrder}`}
                >
                  Edit activation message
                </EditLink>
              </FrameAction>
            </FrameHeader>
            <FramePanel className="max-h-70 min-h-32 overflow-y-auto text-sm whitespace-pre-wrap wrap-break-word">
              {tierActivationMessage ? (
                renderActivationPreview(tierActivationMessage, variablePreviews)
              ) : (
                <span className="text-muted-foreground">
                  AI generated by default by Amy . . .
                </span>
              )}
            </FramePanel>
          </Frame>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button>
                <CircleCheckBigIcon />
                Activate creator
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="max-w-lg">
              <AlertDialogHeader>
                <AlertDialogTitle>Activate creator?</AlertDialogTitle>
                <AlertDialogDescription>
                  We'll email them their unique activation link and onboarding
                  message. This can't be undone from here.
                  {price > 0 &&
                    (processingRate > 0
                      ? ` You're agreeing to ${money(totalPrice)}: ${money(price)} creator fee + ${money(processingFee)} payment processing.`
                      : ` You're agreeing to a ${money(price)} creator fee.`)}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleActivate} disabled={submitting}>
                  <CircleCheckBigIcon />
                  Send activation email
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** One label/amount line of the "Final price" frame. */
function PriceRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4 p-4 py-3">
      <span className={strong ? "text-sm font-medium" : "text-xs text-muted-foreground"}>{label}</span>
      <span className={strong ? "text-sm font-semibold" : "text-sm font-medium"}>{value}</span>
    </div>
  );
}

/**
 * Campaign creators-table adapter: wires the shared view to the table context
 * and campaign-scoped stores. Mounted once in CreatorsTable.
 */
/** "Edit … →" in a section header: leaves the dialog for the page that owns the value. */
function EditLink({ to, children }: { to: string; children: React.ReactNode }) {
  const navigate = useNavigate();
  return (
    <Button variant="link" scope={false} className="group h-auto gap-1 p-0" onClick={() => navigate(to)}>
      {children}
      <ArrowRightIcon className="transition-all group-hover:translate-x-0.5" />
    </Button>
  );
}

export default function ActivateCreatorDialog() {
  const { getToken } = useAuth();
  const { updateCreatorStatus } = useCreatorStatusStore();
  const { approveCreator } = useEveActionsStore();
  const { creators } = useCreatorsStore();
  const { dubDomains } = useDashboardStore();
  const {
    setApprovalDialogCreatorId,
    setLoadingStatusCreators,
    onRefresh,
    campaignFor,
  } = useCreatorsTableContext();
  const { approvalDialogCreatorId } = useCreatorsTableState();

  const creator = creators.find((c) => c._id === approvalDialogCreatorId);
  // The creator's own campaign — org-wide the dialog serves rows from many.
  const campaign = campaignFor(creator);
  const campaignId = campaign?._id ?? null;
  const use_dub = campaign?.use_dub ?? false;
  const isEve = isEveCampaign(campaign);

  const handleConfirm = async ({
    promoCode,
    manualUniqueLink,
    selectedDomain,
    extraVariables,
  }: {
    promoCode: string;
    manualUniqueLink: string;
    selectedDomain: string;
    extraVariables: Record<string, string>;
  }) => {
    if (!approvalDialogCreatorId) return;
    const creatorId = approvalDialogCreatorId;

    posthog.capture("creator_status_update", {
      creatorId,
      newStatus: "pending-activation",
      campaignId,
      manualUniqueLink,
      promoCode,
      selectedDomain,
      extraVariableNames: Object.keys(extraVariables),
    });

    setLoadingStatusCreators((prev) => ({
      ...prev,
      [creatorId]: "pending-activation",
    }));

    setApprovalDialogCreatorId(null);

    try {
      const token = await getToken();
      if (token) {
        if (isEve) {
          // Eve campaigns: the amy service records the decision and sends
          // the activation email (or diverts to the contract phase). No Dub
          // minting — manual link + promo only.
          await approveCreator(
            creatorId,
            { promo_code: promoCode, unique_link: manualUniqueLink },
            token,
          );
        } else {
          await updateCreatorStatus(
            creatorId,
            "pending-activation" as StatusValue,
            promoCode,
            manualUniqueLink,
            selectedDomain,
            token,
            use_dub,
            extraVariables,
          );
        }
      }
    } catch (error) {
      console.error("Error updating creator status:", error);
    } finally {
      setLoadingStatusCreators((prev) => {
        const updated = { ...prev };
        delete updated[creatorId];
        return updated;
      });
      onRefresh();
    }
  };

  return (
    <ActivateCreatorDialogView
      open={!!approvalDialogCreatorId}
      onOpenChange={(o) => {
        if (!o) setApprovalDialogCreatorId(null);
      }}
      username={creator?.username || ""}
      useDub={use_dub && !isEve}
      ccEmails={(campaign?.cc_emails as string[] | string) ?? []}
      currentTierOrder={creator?.current_tier_order ?? 1}
      tiers={campaign?.partnership?.tiers ?? []}
      negotiatedPrice={creator?.negotiated_price}
      currency={campaign?.currency}
      dubDomains={dubDomains}
      campaignId={campaignId ?? ""}
      checkFunding={isEve}
      onConfirm={handleConfirm}
    />
  );
}
